import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  message,
  Popconfirm,
  Alert,
  Row,
  Col,
  Statistic,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';

const { Option } = Select;

const DriverList = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalDrivers: 0,
    availableDrivers: 0,
    unavailableDrivers: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.drivers.getAll({
        search: filters.search,
        status: filters.status,
      });
      
      const driversData = response.data || [];
      setDrivers(Array.isArray(driversData) ? driversData : []);

      // Calculate statistics
      const totalDrivers = driversData.length;
      const availableDrivers = driversData.filter(driver => driver.status === 'available').length;
      const unavailableDrivers = driversData.filter(driver => driver.status === 'unavailable').length;

      setStats({
        totalDrivers,
        availableDrivers,
        unavailableDrivers,
      });
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError(error.response?.data?.message || 'Failed to fetch drivers');
      message.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  const handleDelete = async (id) => {
    try {
      await apiService.drivers.delete(id);
      message.success('Driver deleted successfully');
      fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      message.error('Failed to delete driver');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return 'success';
      case 'assigned':
        return 'processing';
      case 'unavailable':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: '#1890ff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          onClick={() => navigate(`/drivers/${record._id}`)}
        >
          {text}
        </button>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'License Number',
      dataIndex: 'licenseNumber',
      key: 'licenseNumber',
      render: (text) => text || 'N/A',
    },
    {
      title: 'License Expiry',
      dataIndex: 'licenseExpiry',
      key: 'licenseExpiry',
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/drivers/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this driver?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Drivers"
              value={stats.totalDrivers}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Available Drivers"
              value={stats.availableDrivers}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Unavailable Drivers"
              value={stats.unavailableDrivers}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        style={{ marginTop: 16 }}
        title="Drivers"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/drivers/create')}
          >
            New Driver
          </Button>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search drivers"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="available">Available</Option>
            <Option value="assigned">Assigned</Option>
            <Option value="unavailable">Unavailable</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={drivers}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </Card>
    </div>
  );
};

export default DriverList; 